import { createExpiryScheduler, type ExpiryScheduler } from "utils/expiry";
import { buildTrackingPayloadBenefit, buildTrackingPayloadCinema, type TrackingDataset } from "./tracking";
import type { FeatureStatus, ActionName, DataContainer } from "@otto-ec/tracking-bct";
import type { DataTrSubmitTrackingDetail } from "@otto-ec/global-resources/tracking";
import type { Debug } from "@otto-ec/global-resources/debug";
import type { HTMLOcCinemaV1Element } from "@otto-ec/otto-components/cinema";

export const FALLBACK_POSITION = 99;

export function addMiniActionsToVisibilityChanges(detail: DataTrSubmitTrackingDetail<"submitMiniAction">, cinemaDom: HTMLOcCinemaV1Element, actionName: ActionName, parentId: string) {
	if (!cinemaDom.visibleItems) return;
	/*                                                   */
	const benefitsToBeTracked = cinemaDom.visibleItems.filter((element) => element.dataset.trackingState !== "visible");
	if (!benefitsToBeTracked.length) return;

	benefitsToBeTracked
		.filter((benefit) => !!benefit.dataset.tracking)
		/*                                                                           */
		.map((benefit) => {
			const tracking = JSON.parse(benefit.dataset.tracking!) as TrackingDataset;
			const featureId = `${parentId}-${tracking.id}${tracking.position}`;
			benefit.dataset.trackingState = "visible";
			detail.parameterMap.updates.push({ featureId, action: actionName });
		});
	detail.parameterMap.updates.push({ featureId: parentId, action: actionName });
}

/**
 *
 */
export function addLoadedTracking(detail: DataTrSubmitTrackingDetail<"submitMerge">, cinemaDom: HTMLOcCinemaV1Element, featurePosition: number, log: Debug<string>, parentId: string, additionalLabels?: DataContainer) {
	const cinemaTracking = buildTrackingPayloadCinema("loaded", cinemaDom, featurePosition, parentId, additionalLabels);
	if (!cinemaTracking || !detail.parameterMap.features) {
		log.info("missing cinema tracking data");
		detail.abort();
		return;
	}
	const features = detail.parameterMap.features;
	features.push(cinemaTracking);

	const benefits = getBenefitEntries(cinemaDom);

	if (!benefits || !cinemaDom.visibleItems) return log.info("no visible items found on cinema");
	benefits.forEach((benefitElement, i) => {
		let status: FeatureStatus = "hidden";
		if (cinemaDom.visibleItems!.includes(benefitElement)) {
			status = "loaded";
		}

		const benefitTracking = buildTrackingPayloadBenefit(status, benefitElement, cinemaTracking.id);
		if (benefitTracking) {
			features.push(benefitTracking);
		} else {
			log.info("missing benefit tracking data for hidden benefit on position", i);
		}
	});
}

export type ExpirySchedulerMap = Map<string, ExpiryScheduler>;

export function createExpirySchedulerMap(cinemaDom: HTMLOcCinemaV1Element): ExpirySchedulerMap {
	const map = new Map();
	for (const benefit of getBenefitEntries(cinemaDom)) {
		const benefitId = benefit.dataset.benefitId!;
		map.set(benefitId, createExpiryScheduler());
	}
	return map;
}

export function cleanupExpirySchedulerMap(schedulerMap: ExpirySchedulerMap) {
	for (const { cleanup } of schedulerMap.values()) {
		cleanup();
	}
	schedulerMap.clear();
}

export function initBenefitExpiryCleanup(cinemaDom: HTMLOcCinemaV1Element, expirySchedulerMap: ExpirySchedulerMap) {
	const benefits = getBenefitEntries(cinemaDom);

	for (const benefit of benefits) {
		const activeUntil = benefit.dataset.activeUntil;
		const benefitId = benefit.dataset.benefitId;
		if (!activeUntil || !benefitId) {
			return;
		}

		const { scheduler } = expirySchedulerMap.get(benefitId)!;
		scheduler(activeUntil, () => {
			benefit.remove();
			expirySchedulerMap.delete(benefitId);

			if (getBenefitEntries(cinemaDom).length) return;
			cinemaDom.remove();
		});
	}
}

export function getBenefitEntries(cinemaDom: HTMLOcCinemaV1Element): HTMLOcPaperCardV1Element[] {
	return [...cinemaDom.querySelectorAll<HTMLOcPaperCardV1Element>("oc-paper-card-v1")];
}

/**
 */
export function updateSheetUrlParams(cinemaDom: HTMLOcCinemaV1Element, params: Record<string, string>) {
	const sheetCreatingElements = [...cinemaDom.querySelectorAll<HTMLElement>("*[data-oc-sheet-v1-create]")];

	for (const element of sheetCreatingElements) {
		const sheetUrlBase64 = element.dataset["ocSheetV1Create.base64Url"] ?? "";
		const sheetUrl = new URL(atob(sheetUrlBase64), window.origin);

		for (const [key, value] of Object.entries(params)) {
			sheetUrl.searchParams.set(key, value);
		}

		element.dataset["ocSheetV1Create.base64Url"] = btoa(sheetUrl.href);
	}
}

export function getClosestParentByDataAttribute(element: HTMLElement, attribute: string): HTMLElement | null {
	return element.closest(`[data-${attribute}]`);
}

/*                                                                                                                           */
export function getElementPositionInPageContext(element: HTMLElement | null, attribute: string): number {
	if (!element) {
		return FALLBACK_POSITION;
	}

	const positionString = getClosestParentByDataAttribute(element, attribute)?.getAttribute(`data-${attribute}`);
	if (!positionString) {
		return FALLBACK_POSITION;
	}

	const position = parseInt(positionString);
	if (Number.isNaN(position) || position < 0) {
		return FALLBACK_POSITION;
	}
	return position;
}

export function removeQuoteEncoding(entity: string): string {
	return entity.replace(/&quot;/g, '""');
}
